import { useState, useEffect, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import LoadingLayout from 'components/common/layout/LoadingLayout';
import Loading from 'components/common/overlays/Loading';
import ChartLayout from 'components/common/layout/ChartLayout';
import Pentool from 'components/pages/chart/Pentool';
import ChartBottomBtn from 'components/pages/chart/ChartBottomBtn';
import ChartFeedback from 'components/pages/chart/ChartFeedback';
import OralExaminationChartRegister from '../student/OralExaminationChartRegister';
import { STYLES_VARIABLES } from 'constants/styleVariables';
import { EvaluationChartFeedbackDefaultValues } from 'constants/chart/common/chart';
import { TPracticeChartInfo } from 'types/apis/chart/studentPracticeChart';
import { TEvaluationChartInfo } from 'types/apis/chart/studentEvaluationChart';
import { TTool } from 'types/apis/chart/studentPracticeChart';
import { TLine } from 'types/chart/pentool';
import {
  EvaluationChartFeedbackFormField,
  TEvaluationChartFeedbackFormValues,
} from 'types/form/evaluationChart';
import useModal from 'hooks/useModal';
import useGoBack from 'hooks/useGoBack';
import useGetChartInfo, {
  TChartInfoResponse,
} from 'hooks/chart/useGetChartInfo';
import useCanvas from 'hooks/chart/useCanvas';
import {
  postEvaluationChartFeedback,
  putEvaluationChartFeedback,
} from 'apis/professor';
import Konva from 'konva';
import { Layer, Stage, Line } from 'react-konva';
import { useAtomValue } from 'jotai';
import { userInfoAtom } from 'store/auth';
import { toBlob } from 'html-to-image';

const OralExaminationChartFeedback = () => {
  const chartType = 6;
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const category = location.pathname.split('/')[1];
  const { id } = useParams();
  const numericId = Number(id);
  const studentUserId = Number(query.get('studentUserId'));
  const { openModal } = useModal();
  const goBack = useGoBack();

  const userInfo = useAtomValue(userInfoAtom);
  const isProfessor = userInfo?.userRole === 'PROFESSOR';

  const [isLoading, setIsLoading] = useState(true);
  const [isRegister, setIsRegister] = useState(
    location.pathname.includes('register')
  );
  const [isEdit, setIsEdit] = useState(false);
  const [submitEndDate, setSubmitEndDate] = useState<string>('');
  const [tabList, setTabList] = useState<number[]>([]);
  const [activeTabIndex, setActiveTabIndex] = useState<number>(
    query.get('tab') ? Number(query.get('tab')) : 0
  );
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [chartInfoResponse, setChartInfoResponse] =
    useState<TChartInfoResponse | null>(null);
  const [originalFiles, setOriginalFiles] = useState<File | null | undefined>(
    null
  );
  const [stageImage, setStageImage] = useState<any>(null);

  const [isFeedback, setIsFeedback] = useState(false);
  const [feedbackId, setFeedbackId] = useState<number | undefined>(undefined);

  const methods = useForm<TEvaluationChartFeedbackFormValues>({
    defaultValues: EvaluationChartFeedbackDefaultValues,
  });
  const { handleSubmit, setValue, watch, reset, getValues } = methods;

  const { evaluationChartId, feedbackContent, feedbackImage, score } =
    getValues();

  const formValues = watch();
  const initialFormValuesRef = useRef<TEvaluationChartFeedbackFormValues>({
    evaluationChartId,
    feedbackContent,
    feedbackImage,
    score,
  });

  useEffect(() => {
    if (
      initialFormValuesRef.current.evaluationChartId === null &&
      initialFormValuesRef.current.feedbackContent === '' &&
      initialFormValuesRef.current.feedbackImage === '' &&
      initialFormValuesRef.current.score === null
    ) {
      initialFormValuesRef.current = {
        evaluationChartId,
        feedbackContent,
        feedbackImage,
        score,
      };
    }
  }, [evaluationChartId, feedbackContent, feedbackImage, score]);

  const fetchError = () =>
    openModal({
      title: '차트 조회 실패',
      text: '다시 시도해 주세요.',
      onSubmit: goBackToList,
      onClickOutside: goBackToList,
    });

  const handleSuccessGetChartInfo = (data: TChartInfoResponse) => {
    if (!data) {
      setIsLoading(false);
      return;
    }
    setChartInfoResponse(data);

    const chartInfo =
      category === 'practice'
        ? (data.chartInfo as TPracticeChartInfo)
        : (data.chartInfo as TEvaluationChartInfo[])[
            activeTabIndex > 1 ? 1 : 0
          ];
    if (!chartInfo) {
      fetchError();
      return;
    }

    setIsEdit(!!chartInfo);

    if (category !== 'practice') {
      setTabList(() => {
        const infoList = data.chartInfo as TEvaluationChartInfo[];
        const newTabList: number[] = [];

        infoList.forEach((info) => {
          if (info.evaluationRound === '1차' && !!info.evaluationChartId) {
            newTabList.push(0);
          } else if (
            info.evaluationRound === '2차' &&
            !!info.evaluationChartId
          ) {
            newTabList.push(2);
          }
          if (
            !!info.evaluationChartId &&
            info.disclosureStatus &&
            (category === 'feedback' || info.score !== null)
          ) {
            newTabList.push(info.evaluationRound === '1차' ? 1 : 3);
          }
        });
        return newTabList;
      });
    }
    setChartInfo(chartInfo);
  };

  const setChartInfo = (
    chartInfo: TPracticeChartInfo | TEvaluationChartInfo
  ) => {
    const chartData: TEvaluationChartFeedbackFormValues | null =
      (chartInfo as TEvaluationChartFeedbackFormValues) || null;

    const score = Number(chartData?.score) || null;
    setValue(EvaluationChartFeedbackFormField.Score, score);

    const feedbackContent = chartData?.feedbackContent || '';
    setValue(EvaluationChartFeedbackFormField.FeedbackContent, feedbackContent);

    const submitEndDate =
      (chartData as TEvaluationChartInfo)?.submitEndDate || '';
    setSubmitEndDate(submitEndDate);
    checkSubmitEndDate(submitEndDate);
    if (!chartData) {
      reset();
      setIsLoading(false);
      return;
    }
    Object.keys(chartData).forEach((key) => {
      setValue(
        key as keyof TEvaluationChartFeedbackFormValues,
        chartData[key as keyof TEvaluationChartFeedbackFormValues]
      );
    });
    getFeedbackImage(chartData.feedbackImage || null);
    setIsLoading(false);
  };

  const getFeedbackImage = (feedbackImage: string | null) => {
    if (!feedbackImage) {
      setFeedbackLines([]);
      return;
    }
    const drawingData = JSON.parse(JSON.parse(feedbackImage));

    // 데이터에서 라인을 하나라도 가지고 있는 레이어가 있다면 추출
    const includeLineLayers = drawingData.children.filter((layer: any) => {
      return layer.children.some(
        (layerChild: any) => layerChild.className === 'Line'
      );
    });

    const newLines: TLine[] = [];

    includeLineLayers.forEach((lineLayer: any) => {
      lineLayer.children.forEach((lineLayerChild: any) => {
        if (lineLayerChild.className !== 'Line') {
          return;
        }
        // 각 라인으로 새로운 라인 만들기
        const newLine = new Konva.Line(lineLayerChild);

        // 라인의 attrs 에서 points 만 추출
        newLines.push({
          points: newLine.attrs.points,
          strokeColor: newLine.attrs.stroke,
          strokeWidth: newLine.attrs.strokeWidth,
          tool: !newLine.attrs.globalCompositeOperation ? 'pen' : 'eraser',
        });
      });
    });
    // 라인의 attrs 에서 얻은 points 로 state 변경하기
    setFeedbackLines(newLines);
  };

  useGetChartInfo({
    category,
    chartType,
    id: numericId,
    patientId: query.get('patientId') || undefined,
    courseInfoId: query.get('courseInfoId') || undefined,
    chartRound: query.get('chartRound') || undefined,
    onSuccess: handleSuccessGetChartInfo,
  });

  const [tool, setTool] = useState<TTool>('pen');
  const [strokeActive, setStrokeActive] = useState(false);
  const [strokeWidth, setStrokeWidth] = useState(isRegister ? 3 : 0);
  const [strokeColor, setStrokeColor] = useState(STYLES_VARIABLES.BLACK);
  const [lines, setLines] = useState<TLine[]>([]);
  const [feedbackLines, setFeedbackLines] = useState<TLine[]>([]);
  const feedbackStageRef = useRef<any>();
  const feedbackPenLayerRef = useRef<any>();

  const feedbackCanvas = useCanvas({
    tool,
    strokeWidth,
    strokeColor,
    lines: feedbackLines,
    setLines: (newLines) => {
      strokeActive && setFeedbackLines(newLines);
    },
  });

  useEffect(() => {
    setIsRegister(location.pathname.includes('register'));
    setIsSaving(false);
  }, [location.pathname]);

  useEffect(() => {
    setActiveTabIndex(Number(query.get('tab')) || 0);
  }, [location.search]);

  useEffect(() => {
    if (
      (isProfessor && strokeActive) ||
      (isRegister && !isProfessor && strokeActive)
    ) {
      setStrokeWidth(3);
    } else {
      setStrokeWidth(0);
    }
  }, [strokeActive]);

  useEffect(() => {
    if (feedbackLines.length === 0) return;
    setValue(
      'feedbackImage' as keyof TEvaluationChartFeedbackFormValues,
      feedbackStageRef.current?.toJSON() as TEvaluationChartFeedbackFormValues['feedbackImage']
    );
  }, [feedbackLines]);

  useEffect(() => {
    const isChanged =
      initialFormValuesRef.current.evaluationChartId !==
        formValues.evaluationChartId ||
      initialFormValuesRef.current.feedbackContent !==
        formValues.feedbackContent ||
      initialFormValuesRef.current.score !== formValues.score;

    let isChangedCanvas = false;
    if (feedbackLines.length > 0) {
      isChangedCanvas =
        initialFormValuesRef.current.feedbackImage !==
        JSON.stringify(feedbackStageRef.current?.toJSON());
    }

    if (isChanged || isChangedCanvas) {
      setIsFeedback(true);
    } else {
      setIsFeedback(false);
    }
  }, [formValues, initialFormValuesRef.current]);

  // 차트 조회
  useEffect(() => {
    setStrokeActive(false);
    setStrokeColor(STYLES_VARIABLES.BLACK);
    if (!chartInfoResponse) return;
    const chartInfo = (chartInfoResponse.chartInfo as TEvaluationChartInfo[])[
      activeTabIndex > 1 ? 1 : 0
    ];
    setIsLoading(true);
    setChartInfo(chartInfo);
    if (isProfessor) {
      setFeedbackId(chartInfo.feedbackId);
      getFeedbackImage(chartInfo.feedbackImage || null);
    }
  }, [activeTabIndex, chartInfoResponse]);

  const checkSubmitEndDate = (submitEndDate: string) => {
    if (category === 'practice' || !submitEndDate) return;
    const isoFormattedDate = submitEndDate.replace(/\./g, '-');
    const nextDate = new Date(isoFormattedDate);

    if (isNaN(nextDate.getTime())) {
      fetchError();
      return;
    }
    nextDate.setDate(nextDate.getDate() + 1);
    setIsReadOnly(nextDate < new Date());
  };

  useEffect(() => {
    window.onpopstate = goBackToList;
    return () => {
      window.onpopstate = null;
    };
  }, []);

  const goBackToList = () =>
    goBack({
      patientId: query.get('patientId') || undefined,
      studentUserId: query.get('studentUserId') || undefined,
      courseId: query.get('courseId') || undefined,
      courseInfoId: query.get('courseInfoId') || undefined,
      chartRound: query.get('chartRound') || undefined,
    });

  const onError = () => {
    setIsSaving(false);
    openModal({
      title: `차트 평가 ${feedbackId ? '수정' : '등록'} 실패`,
      text: '관리자에게 문의해 주세요.',
    });
  };

  // 차트 평가 등록
  const { mutate: postEvaluationChartFeedbackMutation } = useMutation(
    postEvaluationChartFeedback,
    {
      onSuccess: goBackToList,
      onError,
    }
  );

  // 차트 평가 수정
  const { mutate: putEvaluationChartFeedbackMutation } = useMutation(
    putEvaluationChartFeedback,
    {
      onSuccess: goBackToList,
      onError,
    }
  );

  // 차트 등록
  const onValid = async (data: TEvaluationChartFeedbackFormValues) => {
    let reviewedFiles: File | null | undefined = null;

    setIsSaving(true);
    reviewedFiles = await onCapture(2);
    setIsSaving(false);

    openModal({
      title: '차트 평가를 저장하시겠습니까?',
      text: '진행중인 차트 평가를 저장하시겠습니까?',
      isConfirm: true,
      onSubmit: () => {
        setIsSaving(true);
        const formData = new FormData();

        formData.append(
          'request',
          new Blob(
            [
              JSON.stringify({
                ...(!feedbackId
                  ? {
                      evaluationChartId: data.evaluationChartId,
                    }
                  : {
                      feedbackId,
                    }),
                score: data.score,
                feedbackContent: data.feedbackContent,
                feedbackImage: JSON.stringify(
                  feedbackStageRef.current?.toJSON()
                ),
              }),
            ],
            {
              type: 'application/json',
            }
          )
        );

        if (!reviewedFiles) {
          onError();
          return;
        }
        formData.append('reviewedFiles', reviewedFiles as Blob);
        if (!feedbackId) {
          if (!originalFiles) {
            onError();
            return;
          }
          formData.append('originFiles', originalFiles as Blob);
        }

        if (feedbackId) {
          putEvaluationChartFeedbackMutation(formData);
        } else {
          postEvaluationChartFeedbackMutation(formData);
        }
      },
    });
  };

  // 차트 이미지 캡쳐
  const onCapture = async (type: number) => {
    const chartArea = document.getElementById('chartArea');
    if (!chartArea) return;

    const clonedChartArea = chartArea.cloneNode(true) as HTMLElement;
    clonedChartArea.style.zIndex = '-1';
    clonedChartArea.style.position = 'absolute';
    clonedChartArea.style.top = '0';
    clonedChartArea.style.width = '1184px';
    clonedChartArea.style.paddingBottom = '72px';
    clonedChartArea.style.borderRadius = '0';

    const chartImageArea = clonedChartArea.querySelector(
      '.chart_image_area'
    ) as HTMLElement;
    const chartImage = document.createElement('img');
    const chartImageURL = stageImage.current.toDataURL({
      mimeType: 'image/png',
    });
    chartImage.src = chartImageURL as string;
    chartImage.style.width = '920px';
    chartImage.style.height = '500px';
    if (chartImageArea) {
      chartImageArea.innerHTML = '';
      chartImageArea.appendChild(chartImage);
    }

    const inputs = clonedChartArea.querySelectorAll(
      'input[type="text"], input[type="number"], textarea'
    );
    inputs.forEach((input) => {
      (input as HTMLInputElement).placeholder = '';
    });

    const feedbackImageArea = clonedChartArea.querySelector(
      '.feedback_image_area'
    ) as HTMLElement;
    const feedbackImage = document.createElement('img');
    const feedbackImageURL = feedbackStageRef.current?.toDataURL({
      mimeType: 'image/png',
    });
    feedbackImage.src = feedbackImageURL as string;
    if (feedbackImageArea) {
      feedbackImageArea.innerHTML = '';
      feedbackImageArea.appendChild(feedbackImage);
    }

    document.body.appendChild(clonedChartArea);

    const excludedElements = clonedChartArea.querySelectorAll(
      `.excluded${type}`
    );
    excludedElements.forEach((element) => {
      element.remove();
    });

    return await toBlob(clonedChartArea)
      .then((blob) => {
        if (blob === null) throw new Error('blob is null');
        const file = new File(
          [blob],
          `${type === 1 ? 'original' : 'reviewed'}.png`,
          { type: 'image/png' }
        );
        document.body.removeChild(clonedChartArea);
        return file;
      })
      .catch(() => {
        return null;
      });
  };

  const onCaptureOriginal = () => {
    if (!feedbackId && !originalFiles) {
      onCapture(1).then((file) => setOriginalFiles(file));
    }
  };

  return (
    <>
      {isLoading ? (
        <LoadingLayout />
      ) : (
        <>
          {isSaving && <Loading />}
          <ChartLayout
            category={category}
            chartType={chartType}
            tabList={tabList}
            activeTabIndex={activeTabIndex}
            setActiveTabIndex={setActiveTabIndex}
            submitEndDate={submitEndDate}
            isReadOnly={isReadOnly}
            pentool={true}
            evaluationChartId={evaluationChartId as number}
          >
            <OralExaminationChartRegister
              query={query}
              chartType={chartType}
              category={category}
              id={numericId}
              isProfessor={isProfessor}
              tool={tool}
              setTool={setTool}
              strokeActive={strokeActive}
              setStrokeActive={setStrokeActive}
              strokeWidth={strokeWidth}
              setStrokeWidth={setStrokeWidth}
              strokeColor={strokeColor}
              setStrokeColor={setStrokeColor}
              lines={lines}
              setLines={setLines}
              chartInfoResponse={chartInfoResponse}
              activeTabIndex={activeTabIndex}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
              isRegister={isRegister}
              isEdit={isEdit}
              setSubmitEndDate={setSubmitEndDate}
              isReadOnly={isReadOnly}
              checkSubmitEndDate={checkSubmitEndDate}
              handleSuccessGetChartInfo={handleSuccessGetChartInfo}
              setStageImage={setStageImage}
            />

            <div className="excluded1">
              <FormProvider {...methods}>
                {isProfessor &&
                  (activeTabIndex === 1 || activeTabIndex === 3) && (
                    <Pentool
                      setTool={setTool}
                      strokeActive={strokeActive}
                      setStrokeActive={setStrokeActive}
                      setStrokeWidth={setStrokeWidth}
                      setStrokeColor={setStrokeColor}
                      feedbackLines={feedbackLines}
                      setFeedbackLines={setFeedbackLines}
                      isRegister={
                        (!isProfessor && isRegister && !isReadOnly) ||
                        (isProfessor && !isRegister && isEdit)
                      }
                      activeTabIndex={activeTabIndex}
                    />
                  )}
                {(activeTabIndex === 1 || activeTabIndex === 3) && (
                  <form onSubmit={handleSubmit((data) => onValid(data))}>
                    {/* 교수 피드백 그림 */}
                    <Stage
                      ref={feedbackStageRef}
                      width={1184}
                      height={655}
                      onTouchStart={(e) => {
                        isProfessor &&
                          strokeActive &&
                          feedbackCanvas.handleTouchStart(e);
                      }}
                      onTouchMove={(e) => {
                        isProfessor &&
                          strokeActive &&
                          feedbackCanvas.handleTouchMove(e);
                      }}
                      onTouchEnd={(e) => {
                        isProfessor &&
                          strokeActive &&
                          feedbackCanvas.handleTouchEnd(e);
                      }}
                      className="feedback_image_area"
                    >
                      <Layer ref={feedbackPenLayerRef}>
                        {feedbackLines.map((line, i) => (
                          <Line
                            key={i}
                            points={line.points}
                            stroke={line.strokeColor}
                            strokeWidth={line.strokeWidth}
                            tension={0.5}
                            lineCap="round"
                            lineJoin="round"
                            globalCompositeOperation={
                              line.tool === 'eraser'
                                ? 'destination-out'
                                : 'source-over'
                            }
                          />
                        ))}
                      </Layer>
                    </Stage>
                    <ChartFeedback
                      isProfessor={isProfessor}
                      evaluationChartId={numericId as number}
                      userId={studentUserId as number}
                      feedbackId={feedbackId as number}
                      handleFocus={onCaptureOriginal}
                    />
                    <ChartBottomBtn
                      category={category}
                      id={numericId}
                      courseId={query.get('courseId') || undefined}
                      patientId={query.get('patientId') || undefined}
                      courseInfoId={query.get('courseInfoId') || undefined}
                      chartRound={query.get('chartRound') || undefined}
                      studentUserId={query.get('studentUserId') || undefined}
                      isRegister={isRegister}
                      isEdit={isEdit}
                      isReadOnly={isReadOnly}
                      chartName={chartInfoResponse?.chartName}
                      feedbackId={feedbackId}
                      isFeedback={isFeedback}
                    />
                  </form>
                )}
              </FormProvider>
            </div>
          </ChartLayout>
        </>
      )}
    </>
  );
};

export default OralExaminationChartFeedback;
